import { BASE_PIXEL } from './constants';
import './SubtitleApi';
import SubtitleStore from './SubtitleStore';

import Request from '@hotmart/request';

class SubtitleService {
  uploadSubtitle(mediaCode, languageCode, subtitle) {
    return Request('media.subtitle.upload', {
      mediaCode,
      subtitle,
      languageCode
    });
  }

  removeSubtitle(mediaCode, subtitleCode) {
    return Request('media.subtitle.delete', { mediaCode, subtitleCode });
  }

  fetchSubtitleSupportedTranscriptionLangs() {
    return Request('media.subtitle.transcribe.supported_langs');
  }

  fetchSubtitleTrack(mediaCode, subtitleCode) {
    return Request('media.subtitle.track', { mediaCode, subtitleCode });
  }

  fetchSubtitleList(mediaCode) {
    return Request('media.subtitle.list', { mediaCode });
  }

  transcribeMedia(mediaCode) {
    return Request('media.subtitle.transcribe', { mediaCode });
  }

  translateSubtitle(subtitleCode, mediaCode, languageCode) {
    return Request('media.subtitle.translate', {
      subtitleCode,
      mediaCode,
      languageCode
    });
  }

  saveSubtitle(mediaCode, subtitleCode) {
    const { cues } = SubtitleStore;

    return Request('media.subtitle.update_cues', {
      mediaCode,
      subtitleCode,
      cues
    });
  }

  approveSubtitle(mediaCode, subtitleCode) {
    return Request('media.subtitle.approve', { mediaCode, subtitleCode });
  }

  toggleSubtitle(active, mediaCode, subtitleCode) {
    return Request('media.subtitle.update', {
      active,
      mediaCode,
      subtitleCode
    });
  }

  retrySubtitle(mediaCode, subtitleCode) {
    return Request('media.subtitle.retry', { mediaCode, subtitleCode });
  }

  convertSecondsToBasePixels(targetSeconds) {
    return this.convertSecondsToPixels(
      targetSeconds,
      BASE_PIXEL * SubtitleStore.zoom
    );
  }

  convertSecondsToPixels(targetSeconds, componentWidth, duration = 1) {
    return (targetSeconds * componentWidth) / duration;
  }

  convertBasePixelsToSeconds(targetPixel) {
    return this.convertPixelsToSeconds(
      targetPixel / SubtitleStore.zoom,
      BASE_PIXEL
    );
  }

  convertPixelsToSeconds(targetPixel, componentWidth, duration = 1) {
    return (duration * targetPixel) / componentWidth;
  }

  cueToPrecision(cue) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    cue.startTime = parseFloat(cue.startTime.toPrecision(4));
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    cue.endTime = parseFloat(cue.endTime.toPrecision(4));

    return cue;
  }

  isCueDifferent = (a, b) => {
    return !(
      a.startTime === b.startTime &&
      a.endTime === b.endTime &&
      a.content === b.content
    );
  };

  getLessDifferentTime = (prev, curr, time, prop) =>
    Math.abs(curr[prop] - time) < Math.abs(prev[prop] - time) ? curr : prev;

  getLessDifferentCue(cues, time, prop) {
    return cues.length >= 1
      ? cues.reduce((prev, curr) =>
          this.getLessDifferentTime(prev, curr, time, prop)
        )
      : {};
  }

  getPrevCue(time, currentId) {
    let { cues } = SubtitleStore;

    cues = cues.filter((cue) => cue.endTime < time && cue.id !== currentId);
    return this.getLessDifferentCue(cues, time, 'endTime');
  }

  getNextCue(time, currentId) {
    let { cues } = SubtitleStore;

    cues = cues.filter((cue) => cue.startTime > time && cue.id !== currentId);
    return this.getLessDifferentCue(cues, time, 'startTime');
  }

  getOverridingCue(startTime) {
    const { cues } = SubtitleStore;

    const overridingCue = cues.find(
      (cue) => startTime >= cue.startTime && startTime < cue.endTime
    );

    return overridingCue && { ...overridingCue };
  }

  cueTimeCheck(cue) {
    if (this.isCueTimeInvalid(cue)) {
      cue = this.cueInvalidTimeCheck(cue);
    }

    cue = this.cueOverlappingTimeCheck(cue);

    return cue;
  }

  isCueTimeInvalid = (cue) => {
    return this.isCueNegative(cue) || this.isCueGreaterThanDuration(cue);
  };

  isCueNegative(cue) {
    return cue.startTime < 0;
  }

  isCueGreaterThanDuration(cue) {
    return cue.endTime > SubtitleStore.duration;
  }

  cueInvalidTimeCheck(cue) {
    const { duration } = SubtitleStore;

    if (this.isCueNegative(cue)) {
      cue.startTime = 0;
    } else if (this.isCueGreaterThanDuration(cue)) {
      cue.endTime = duration;
    }

    return cue;
  }

  cueOverlappingTimeCheck(cueCheck) {
    const prevCue = this.getPrevCue(cueCheck.endTime, cueCheck.id);
    const nextCue = this.getNextCue(cueCheck.startTime, cueCheck.id);

    const isOverlappingEnd = nextCue && cueCheck.endTime > nextCue.startTime;
    const isOverlappingStart = prevCue && cueCheck.startTime < prevCue.endTime;

    if (isOverlappingEnd) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      cueCheck.endTime = nextCue.startTime - 0.01;
    } else if (isOverlappingStart) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      cueCheck.startTime = prevCue.endTime + 0.01;
    }

    return cueCheck;
  }
}

export default new SubtitleService();
